import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ShieldAlert } from 'lucide-react';
import { AnimatePresence, MotionConfig, motion } from 'framer-motion';
import { useSecurity } from '../../context/SecurityContext';
import StepUpModal from '../StepUpModal';
import GlobalHeader from './GlobalHeader';
import OfflineBanner from '../Common/OfflineBanner';
import { getPinLockStatus, recordFailedAttempt, resetFailedAttempts } from '@services/pinRateLimitService';
import { getSecuritySettings } from '@services/securitySettingsService';
import { enqueueNotificationRequest } from '@services/fcmService';

interface AdminLayoutProps {
  children?: React.ReactNode;
  activeTab: string | null;
  setActiveTab: (tab: string | null) => void;
  user: any;
  offlineMode: boolean;
  handleLogout: () => void;
  isOwner: boolean;
  brandInfo: any;
  isKioskMode?: boolean;
  isGridView: boolean;
  setIsGridView: (value: boolean) => void;
  managerPermissions?: string[];
  notificationCount?: number;
  footer?: React.ReactNode;
}

/**
 * AdminLayout - The main shell of the application with security step-up.
 */
export const AdminLayout: React.FC<AdminLayoutProps> = ({
  children,
  activeTab,
  setActiveTab,
  user,
  offlineMode,
  handleLogout,
  isOwner,
  brandInfo,
  isKioskMode,
  isGridView,
  setIsGridView,
  footer,
}) => {
  const { setStepUpUnlockedAt, isStepUpValid, captureImplicitSelfie } = useSecurity();
  const [stepUpModalOpen, setStepUpModalOpen] = useState(false);
  const [stepUpPendingTab, setStepUpPendingTab] = useState<string | null>(null);
  const [stepUpExpectedPin, setStepUpExpectedPin] = useState('');
  const [pinLockStatus, setPinLockStatus] = useState<{ locked: boolean; remainingMs: number }>({ locked: false, remainingMs: 0 });

  // Load PIN settings when modal opens
  useEffect(() => {
    if (stepUpModalOpen && user?.uid) {
      getSecuritySettings(user.uid)
        .then((d: any) => setStepUpExpectedPin(d.adminPin || ''))
        .catch(() => setStepUpExpectedPin(''));
    }
  }, [stepUpModalOpen, user?.uid]);

  // Handle PIN lock status
  useEffect(() => {
    if (!stepUpModalOpen) return;
    
    const status = getPinLockStatus('admin_pin');
    // Ensure we don't cause infinite loops by checking if state actually needs to change
    setPinLockStatus(prev => {
      if (prev.locked !== status.locked || prev.remainingMs !== (status.remainingMs || 0)) {
        return { locked: status.locked, remainingMs: status.remainingMs || 0 };
      }
      return prev;
    });

    let t: any = null;
    if (status.locked || pinLockStatus.locked) {
      t = setInterval(() => {
        const s = getPinLockStatus('admin_pin');
        setPinLockStatus({ locked: s.locked, remainingMs: s.remainingMs || 0 });
        if (!s.locked && t) clearInterval(t);
      }, 1000); // Ticking every 1s instead of 10s for smooth UX
    }
    
    return () => { if (t) clearInterval(t); };
  }, [stepUpModalOpen, pinLockStatus.locked]);

  const onStepUpConfirm = useCallback(() => {
    resetFailedAttempts('admin_pin');
    setStepUpUnlockedAt(Date.now());
    if (stepUpPendingTab) {
        setIsGridView(false);
        setActiveTab(stepUpPendingTab);
    }
    setStepUpModalOpen(false);
    setStepUpPendingTab(null);
  }, [stepUpPendingTab, setStepUpUnlockedAt, setActiveTab, setIsGridView]);

  const onStepUpFailedAttempt = useCallback(() => {
    import('@services/createAuditLog').then(({ createAuditLog }) => {
      createAuditLog('admin_pin_failure', 'CRITICAL', 'pin_failure', {});
    }).catch(() => { });
    
    // Security Fix: Gửi cảnh báo tới Owner (Chủ quán) thay vì User hiện tại (Nhân viên đang cố nhập sai PIN)
    const targetOwnerId = brandInfo?.ownerId || user?.uid;
    recordFailedAttempt('admin_pin', targetOwnerId, ((ownerId: any) => {
      enqueueNotificationRequest(ownerId as string, 'pin_lock', { reason: 'pin_brute_force', message: 'Mã PIN Quản trị nhập sai 5 lần. Đã khóa 15 phút.' });
    }) as any);
    
    // Zero-Trust: Chụp ảnh ngầm (Implicit Selfie) của người đang nhập sai PIN
    if (captureImplicitSelfie) {
      captureImplicitSelfie(user?.uid || 'anonymous', 'Cảnh báo bảo mật: Nhập sai mã PIN Quản trị').catch(() => {});
    }
    
    const updatedStatus = getPinLockStatus('admin_pin');
    setPinLockStatus({ locked: updatedStatus.locked, remainingMs: updatedStatus.remainingMs || 0 });
  }, [user?.uid, brandInfo?.ownerId]);

  const onStepUpCancel = useCallback(() => {
    setStepUpModalOpen(false);
    setStepUpPendingTab(null);
  }, []);

  const [newDeviceAlert, setNewDeviceAlert] = useState(false);
  useEffect(() => {
    if (!user?.uid || !isOwner) return;
    import('@services/securitySettingsService').then(({ addOrUpdateDevice }) => {
      addOrUpdateDevice(user.uid).then((r: any) => {
        if (r?.isNewDevice && typeof sessionStorage !== 'undefined' && sessionStorage.getItem('new_device_alert_shown') !== '1') {
          setNewDeviceAlert(true);
        }
      });
    });
  }, [user?.uid, isOwner]);

  const dismissNewDeviceAlert = useCallback(() => {
    try { sessionStorage.setItem('new_device_alert_shown', '1'); } catch { /* ignore */ }
    setNewDeviceAlert(false);
  }, []);

  return (
    <MotionConfig reducedMotion="user">
       <div className="flex flex-col h-[100dvh] overflow-hidden bg-slate-950 text-white selection:bg-indigo-500/30">
          {/* Static Premium Background - Optimized for POS performance (Removed pulse animation) */}
          <div className="fixed inset-0 overflow-hidden pointer-events-none z-0 isolation-auto">
            <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] md:w-[40%] md:h-[40%] bg-indigo-600/20 rounded-full blur-[80px] md:blur-[120px]" />
            <div className="absolute top-[20%] right-[-10%] w-[40%] h-[50%] md:w-[30%] md:h-[40%] bg-purple-600/10 rounded-full blur-[60px] md:blur-[100px]" />
            <div className="absolute bottom-[-10%] right-[10%] w-[50%] h-[50%] md:w-[40%] md:h-[40%] bg-emerald-600/10 rounded-full blur-[80px] md:blur-[120px]" />
          </div>

          {newDeviceAlert && (
            <div className="fixed top-0 left-0 right-0 z-[100] bg-amber-500 text-amber-950 px-4 py-3 flex items-center justify-between gap-4 shadow-lg border-b-2 border-amber-600">
              <span className="flex items-center gap-2 font-semibold">
                <ShieldAlert size={22} className="flex-shrink-0" />
                Phát hiện đăng nhập từ trình duyệt/thiết bị mới. Nếu không phải bạn, hãy đổi Mã PIN ngay tại Cài đặt Bảo mật.
              </span>
              <button type="button" onClick={dismissNewDeviceAlert} className="px-4 py-2 rounded-lg bg-amber-700 text-white font-bold hover:bg-amber-800">
                Đã hiểu
              </button>
            </div>
          )}

          {offlineMode && (
            <div className="fixed top-0 left-0 right-0 z-50">
              <OfflineBanner />
            </div>
          )}

          {!isKioskMode && (
            <GlobalHeader
              brandInfo={brandInfo}
            />
          )}

          <main
            className="flex-1 flex flex-col min-h-0 h-full overflow-y-auto overflow-x-hidden relative custom-scrollbar main-scroll-area pb-10 px-4 md:fluid-container-padding"
          >
            {!isKioskMode && (
              <div className={`flex-shrink-0 ${offlineMode ? 'h-[104px]' : 'h-16'}`} />
            )}
            
            <AnimatePresence mode="wait">
                 {children}
            </AnimatePresence>
          </main>

          {footer}

          {stepUpModalOpen && (
            <StepUpModal
              isOpen={stepUpModalOpen}
              onCancel={onStepUpCancel}
              onConfirm={onStepUpConfirm}
              onFailedAttempt={onStepUpFailedAttempt}
              expectedPin={stepUpExpectedPin}
              isLocked={pinLockStatus.locked}
              lockRemainingMs={pinLockStatus.remainingMs}
              emptyMessage="Chưa cấu hình mã PIN Quản trị. Vào Cài đặt Bảo mật."
              title="Quản trị tối cao"
              subtitle="Vui lòng nhập mã PIN để truy cập dữ liệu nhạy cảm."
            />
          )}
       </div>
    </MotionConfig>
  );
};

export default AdminLayout;
